import { getEmbedConfig } from './embed-config'

// When the app is embedded in the Sendhub webclient, our bundle is injected into
// the *parent* page's DOM (not an iframe), so `window.location.origin` is the
// embedding origin (e.g. dev.sendhub.com) rather than where our app + API are
// actually served. Derive the origin from our own bundle's <script> src instead,
// so API calls hit the integrations backend, not the embedding host.
function originFromOwnScript(): string | undefined {
  if (typeof document === 'undefined') return undefined
  const ourScript = document.querySelector('script[src*="/main."]') as HTMLScriptElement | null
  if (!ourScript?.src) return undefined
  try {
    return new URL(ourScript.src).origin
  } catch {
    return undefined
  }
}

/**
 * Canonicalize a configured integrations base URL to the bare origin.
 *
 * The same `INTEGRATIONS_BASE_URL` env var is consumed by the backend (which
 * wants the value to include the `/api/v2/` API prefix) and by this frontend
 * (which appends `/api/v2`, `/proxy`, `/zapier-oauth`, image paths, etc. to a
 * bare origin). To avoid forcing everyone to keep two spellings in sync, accept
 * EITHER form here and strip a trailing `/api/v2` (and any trailing slashes) so
 * downstream concatenation never produces a doubled `/api/v2//api/v2/...` path
 * (which nginx rejects with 400).
 *
 * Examples:
 *   "http://localhost:8000"            -> "http://localhost:8000"
 *   "http://localhost:8000/"           -> "http://localhost:8000"
 *   "http://localhost:8000/api/v2"     -> "http://localhost:8000"
 *   "http://localhost:8000/api/v2/"    -> "http://localhost:8000"
 *   "https://stg-int.sendhub.com"      -> "https://stg-int.sendhub.com"
 */
export function normalizeIntegrationsBaseUrl(raw: string): string {
  return raw
    .trim()
    .replace(/\/+$/, '') // drop trailing slash(es)
    .replace(/\/api\/v2$/i, '') // drop a trailing /api/v2 prefix-suffix
    .replace(/\/+$/, '') // drop any newly-exposed trailing slash
}

/**
 * Match the API base URL's scheme to the page's. An `http://` base on an
 * `https://` page is unusable: the browser blocks it as mixed content, and the
 * dev nginx serves HTTPS only — a plain-HTTP request to its port is answered
 * with `400 The plain HTTP request was sent to HTTPS port`, which surfaces as a
 * spurious auth failure. So when the page is HTTPS, force the base to HTTPS too.
 * `pageProtocol` is injected (rather than read from `window`) so this stays pure
 * and unit-testable.
 *
 * Examples (pageProtocol = "https:"):
 *   "http://localhost:8000"  -> "https://localhost:8000"
 *   "https://localhost:8000" -> "https://localhost:8000"
 * Examples (pageProtocol = "http:"):
 *   "http://localhost:8000"  -> "http://localhost:8000"
 */
export function matchPageScheme(url: string, pageProtocol: string | undefined): string {
  if (pageProtocol === 'https:' && url.startsWith('http://')) {
    return `https://${url.slice('http://'.length)}`
  }
  return url
}

export const INTEGRATIONS_BASE_URL = normalizeIntegrationsBaseUrl(
  matchPageScheme(
    (() => {
      // Nx only inlines NX_-prefixed env vars into the browser bundle, so accept
      // either spelling.
      const envOverride = process.env['INTEGRATIONS_BASE_URL'] || process.env['NX_INTEGRATIONS_BASE_URL']

      switch (process.env['NODE_ENV']) {
        case 'production':
          if (envOverride) {
            console.log(`[config] Using INTEGRATIONS_BASE_URL from environment: "${envOverride}"`)
            return envOverride
          }
          console.log(`[config] NODE_ENV is "production", using production INTEGRATIONS_BASE_URL`)
          return 'https://integrations.sendhub.com'
        case 'staging':
          if (envOverride) {
            console.log(`[config] Using INTEGRATIONS_BASE_URL from environment: "${envOverride}"`)
            return envOverride
          }
          console.log(`[config] NODE_ENV is "staging", using staging INTEGRATIONS_BASE_URL`)
          return 'https://stg-int.sendhub.com'
        default: {
          // Dev: the SPA and API are served same-origin behind one nginx, so the
          // origin our bundle was actually served from is always the correct API
          // base. Prefer it over a build-time env override — that override is
          // frequently a stale `http://localhost:8000` value, and using it when the
          // app is served from a real *.sendhub.com host (e.g. e2e against the dev
          // stack) breaks both the scheme (plain HTTP to the HTTPS-only port) and
          // cookie domains (a `.sendhub.com` auth cookie can't be set from a
          // `localhost` response).
          //
          // originFromOwnScript() resolves the embedded case (our bundle injected
          // into a parent page, where window.location.origin would be the embedding
          // host). It only matches when the <script src> is absolute, so in the
          // standalone dev/e2e case it returns undefined and we use the page origin.
          if (typeof window !== 'undefined') {
            // A host that injected us says where we are served from; that beats
            // guessing from <script> tags (the React SPA has its own main.* entry).
            const servingOrigin = getEmbedConfig().origin ?? originFromOwnScript() ?? window.location.origin
            console.log(`[config] NODE_ENV is "development", using serving origin "${servingOrigin}" for INTEGRATIONS_BASE_URL`)
            return servingOrigin
          }
          // Non-browser (e.g. SSR/build) fallback: honor the env override if present.
          if (envOverride) {
            console.log(`[config] Using INTEGRATIONS_BASE_URL from environment: "${envOverride}"`)
            return envOverride
          }
          return 'http://localhost:8000'
        }
      }
    })(),
    typeof window !== 'undefined' ? window.location?.protocol : undefined
  )
)

export const SENDHUB_BASE_URL = (() => {
  // The environment variable should override the switch statement. Nx only
  // inlines NX_-prefixed vars into the browser bundle, so accept either
  // spelling (same reason as INTEGRATIONS_BASE_URL above).
  const envOverride = process.env['SENDHUB_BASE_URL'] || process.env['NX_SENDHUB_BASE_URL']
  if (envOverride) return envOverride

  switch (process.env['NODE_ENV']) {
    case 'production':
      return 'https://api.sendhub.com'
    default:
      return 'https://app-s4.sendhub.com'
  }
})()

/**
 * Name of the SendHub session cookie, which varies per environment (inforeach's
 * SESSION_COOKIE_NAME: `sh_session_dev`, `sh_session_staging`,
 * `sh_session_production`). We read it from the embedding page's cookies and
 * forward the value as X-SH-Session-Key, so it must match what that webclient
 * set. Staging builds are also NODE_ENV=production, so staging must set
 * SENDHUB_AUTHTOKEN_COOKIE_NAME explicitly.
 */
export const SENDHUB_SESSION_COOKIE_NAME =
  process.env['SENDHUB_AUTHTOKEN_COOKIE_NAME'] ||
  process.env['NX_SENDHUB_AUTHTOKEN_COOKIE_NAME'] ||
  (process.env['NODE_ENV'] === 'production' ? 'sh_session_production' : 'sh_session_dev')
